import { Placement } from "@floating-ui/react";
import React, { ReactNode, useContext } from "react";
import { ChevronDown } from "lucide-react";
import { ButtonVariant } from "../button";
import Dropdown, { DropdownContext } from "../dropdown";
import NavigationLink, {
  NavigationItem,
  NavigationItemProps,
} from "./navigationLink";

export interface NavigationMenuProps extends NavigationMenuItemProps {
  label: ReactNode;
  menuPlacement?: Placement;
  onItemClick?: () => void;
  endAdornment?: ReactNode;
  buttonStyle?: boolean;
  variant?: ButtonVariant;
}

const NavigationMenu = ({
  children,
  label,
  menuPlacement,
  endAdornment,
  buttonStyle = false,
  variant,
  onItemClick,
  ...rest
}: NavigationMenuProps) => {
  return (
    <Dropdown menuPlacement={menuPlacement}>
      <Dropdown.Toggle
        noStyle={!buttonStyle}
        variant={variant}
        noIndicator={!buttonStyle}
      >
        {!buttonStyle && (
          <NavigationItem
            {...rest}
            endAdornment={endAdornment ?? <ChevronDown size="16" />}
          >
            {label}
          </NavigationItem>
        )}
        {buttonStyle && label}
      </Dropdown.Toggle>
      <Dropdown.Menu className="pb-0">
        {React.Children.map(children, (child) => {
          if (
            React.isValidElement(child) &&
            child.type === NavigationMenuItem
          ) {
            return React.cloneElement(child, {
              onClick: () => {
                child.props.onClick?.();
                onItemClick?.();
              },
            } as NavigationMenuItemProps);
          }
          return child;
        })}
        <div className="sticky bottom-0 pb-2 bg-white" />
      </Dropdown.Menu>
    </Dropdown>
  );
};

export interface NavigationMenuItemProps extends NavigationItemProps {
  footer?: boolean;
}

export const NavigationMenuItem = (
  props: Readonly<NavigationMenuItemProps>,
) => {
  const { footer, onClick: onClickParam, ...rest } = props;
  const { close } = useContext(DropdownContext);
  const onClick = () => {
    onClickParam?.();
    close();
  };

  if (footer) {
    return (
      <div className="sticky bottom-2 bg-white">
        <NavigationItem {...rest} onClick={onClick} />
      </div>
    );
  }

  return <NavigationLink {...rest} onClick={onClick} />;
};

export default Object.assign(NavigationMenu, {
  Item: NavigationMenuItem,
});
